import { useState, useRef, useEffect, useCallback } from 'react';
import { useMateriais } from '@/hooks/useDB';
import { type Material } from '@/db';
import { useAuth } from '@/hooks/useAuth';
import { canAccessMaterial, canManageMasterData } from '@/auth/access';

interface ProdutoAutocompleteProps {
  onAdd: (material: Material) => void;
}

export default function ProdutoAutocomplete({ onAdd }: ProdutoAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { materiais } = useMateriais(debouncedQuery);
  const { user } = useAuth();
  const canSeeCost = canManageMasterData(user);

  const filteredMateriais = debouncedQuery.trim()
    ? materiais.filter((material) => canAccessMaterial(user, material))
        .filter(
          (m) =>
            m.codigo.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
            m.descricao.toLowerCase().includes(debouncedQuery.toLowerCase()),
        )
        .slice(0, 15)
    : [];

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback(
    (material: Material) => {
      onAdd(material);
      setQuery('');
      setOpen(false);
    },
    [onAdd],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || filteredMateriais.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filteredMateriais.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredMateriais[highlightIndex]);
    }
  };

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div ref={containerRef} className="relative">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setHighlightIndex(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar produto por código ou descrição..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-background-200/70 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50 transition-all"
          />
        </div>
      </div>

      {open && filteredMateriais.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-background-200/70 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          {filteredMateriais.map((material, idx) => (
            <button
              key={material.id}
              onClick={() => handleSelect(material)}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors cursor-pointer border-b border-background-100 last:border-b-0 ${
                idx === highlightIndex
                  ? 'bg-primary-50'
                  : 'hover:bg-background-50'
              }`}
            >
              <div className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-mono font-semibold text-foreground-600">
                  {material.origemFiscal?.charAt(0) || '?'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950 truncate">{material.descricao}</p>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="font-mono">{material.codigo}</span>
                  {canSeeCost && <><span>·</span><span>Custo: R$ {formatCurrency(material.custoReposicao)}</span></>}
                  <span>·</span>
                  <span>IPI: {material.ipi}%</span>
                  <span>·</span>
                  <span>ICMS: {(material.icmsRs * 100).toFixed(0)}%</span>
                </div>
              </div>
              {material.estoque != null && (
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-semibold whitespace-nowrap">
                  Est: {material.estoque.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                </span>
              )}
              <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-add-line text-white text-xs" />
              </div>
            </button>
          ))}
        </div>
      )}

      {open && query.trim() && filteredMateriais.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-background-200/70 rounded-lg shadow-lg z-50 p-4 text-center">
          <p className="text-sm text-foreground-500">Nenhum produto encontrado</p>
        </div>
      )}
    </div>
  );
}
